/**
 * REST client for the front-end screens (accounts and pilots). Errors carry the HTTP status
 * and the server's `{ error }` message; network failures surface as status 0.
 */
import type { ApiError, AuthResponse, CharacterSummary, ClassId } from '@mirrorscar/shared';

export const TOKEN_KEY = 'mirrorscar.token';
export const USER_KEY = 'mirrorscar.username';
export const LAST_CHAR_KEY = 'mirrorscar.lastCharacter';

export class ApiFailure extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export async function apiRequest<T>(method: string, path: string, body?: unknown, token?: string | null): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiFailure(0, 'Cannot reach the Mirrorscar server. Check that it is running.');
  }
  let data: unknown = null;
  const text = await res.text().catch(() => '');
  if (text) {
    try { data = JSON.parse(text); } catch { data = null; }
  }
  if (!res.ok) {
    const msg = data && typeof data === 'object' && typeof (data as ApiError).error === 'string'
      ? (data as ApiError).error
      : `Server error (${res.status})`;
    throw new ApiFailure(res.status, msg);
  }
  return data as T;
}

/** /api/health: who is online, the build version and whether registering needs an invite code. */
export interface ServerHealth {
  ok: boolean;
  players: number;
  version?: string;
  /** When the running build was made (ISO); absent in development. */
  built?: string;
  invite?: boolean;
}

/** A build time as a short date ("7 Oct 2026"), or '' when unknown. */
export function buildDate(iso: string | null | undefined): string {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

export const api = {
  login: (username: string, password: string) => apiRequest<AuthResponse>('POST', '/api/login', { username, password }),
  register: (username: string, password: string, invite?: string) => apiRequest<AuthResponse>('POST', '/api/register', { username, password, ...(invite ? { invite } : {}) }),
  logout: (token: string) => apiRequest<{ ok: boolean }>('POST', '/api/logout', undefined, token),
  characters: (token: string) => apiRequest<CharacterSummary[]>('GET', '/api/characters', undefined, token),
  create: (token: string, name: string, cls: ClassId) => apiRequest<CharacterSummary>('POST', '/api/characters', { name, cls }, token),
  remove: (token: string, id: string) => apiRequest<{ ok: boolean }>('DELETE', `/api/characters/${encodeURIComponent(id)}`, undefined, token),
  health: () => apiRequest<ServerHealth>('GET', '/api/health'),
};

/** Client-side mirror of the server's pilot name rule (3-16 letters/digits/space/_/-, one letter). */
export function validatePilotName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name.length < 3 || name.length > 16) return 'Pilot names are 3-16 characters long.';
  if (!/^[A-Za-z0-9 _-]+$/.test(name)) return 'Use letters, digits, spaces, _ or - only.';
  if (!/[A-Za-z]/.test(name)) return 'Include at least one letter.';
  return null;
}

/** The same key as saved before the rename from SpaceFare (`spacefare.token`), or null. */
export function legacyStorageKey(key: string): string | null {
  return key.startsWith('mirrorscar.') ? `spacefare.${key.slice('mirrorscar.'.length)}` : null;
}

/** localStorage access that tolerates disabled storage. Reads fall back to the pre-rename key. */
export const store = {
  get(key: string): string | null {
    try {
      const legacy = legacyStorageKey(key);
      return localStorage.getItem(key) ?? (legacy ? localStorage.getItem(legacy) : null);
    } catch { return null; }
  },
  set(key: string, value: string): void {
    try { localStorage.setItem(key, value); } catch { /* storage unavailable */ }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
      const legacy = legacyStorageKey(key);
      if (legacy) localStorage.removeItem(legacy);
    } catch { /* storage unavailable */ }
  },
};
